<template>
  <div
    :class="[
      'ecx-popover relative',
      matchTriggerWidth ? 'block w-full min-w-0' : 'inline-block',
    ]"
    ref="containerRef"
  >
    <!-- Trigger -->
    <div 
      ref="triggerRef"
      @click="toggleOnClick && toggle()"
    >
      <slot name="trigger" :isOpen="isOpen" :toggle="toggle" />
    </div>
    
    <!-- Popover content -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-150 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="isOpen"
          ref="popoverRef"
          :class="[
            'fixed rounded-lg bg-white shadow-lg border border-grey-200',
            popoverMaxHeightPx != null ? 'overflow-x-hidden overflow-y-auto' : 'overflow-hidden',
            popoverZIndexClass,
            widthClass,
          ]"
          :style="popoverStyle"
        >
          <!-- Header -->
          <div 
            v-if="title || $slots.header" 
            :class="[
              'px-4 py-3 border-b bg-grey-50 border-grey-100',
              headerClass,
            ]"
          >
            <slot name="header">
              <h3 class="text-sm font-medium text-default-black">{{ title }}</h3>
            </slot>
          </div>
          
          <!-- Content -->
          <div :class="['px-3 py-3', contentClass]">
            <slot :close="close" />
          </div>
          
          <!-- Footer -->
          <div 
            v-if="$slots.footer" 
            :class="[
              'px-4 py-3 border-t bg-grey-50 border-grey-100',
              footerClass,
            ]"
          >
            <slot name="footer" :close="close" />
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import type { CSSProperties } from 'vue'

export type PopoverPosition = 'top' | 'bottom' | 'left' | 'right'
export type PopoverAlign = 'start' | 'center' | 'end'

export interface PopoverProps {
  /** Controle externo do estado */
  modelValue?: boolean
  /** Título do popover */
  title?: string
  /** Posição preferida */
  position?: PopoverPosition
  /** Alinhamento */
  align?: PopoverAlign
  /** Largura (classe Tailwind ou valor) */
  width?: string
  /** Fechar ao clicar fora */
  closeOnClickOutside?: boolean
  /** Abrir/fechar ao clicar no trigger */
  toggleOnClick?: boolean
  /** Offset em pixels */
  offset?: number
  /** Classes customizadas */
  headerClass?: string
  contentClass?: string
  footerClass?: string
  /** z-index do painel teleported (ex.: z-stack-popover acima de drawer/modal) */
  popoverZIndexClass?: string
  /** Largura do painel igual à do trigger (via getBoundingClientRect), ignora `width` em classe */
  matchTriggerWidth?: boolean
  /** Com `position` top/bottom: abre do lado com mais espaço no container rolável (ou viewport). */
  autoFlipVertical?: boolean
  /**
   * Recorta o painel ao retângulo do primeiro ancestral com `data-ecx-popover-boundary`
   * ou scrollport (`overflow` auto/scroll/overlay). Evita sobrepor área fora da tabela/cartão.
   */
  containInScrollParent?: boolean
}

const props = withDefaults(defineProps<PopoverProps>(), {
  position: 'bottom',
  align: 'end',
  width: 'w-64',
  closeOnClickOutside: true,
  toggleOnClick: true,
  offset: 8,
  headerClass: '',
  contentClass: '',
  footerClass: '',
  popoverZIndexClass: 'z-50',
  matchTriggerWidth: false,
  autoFlipVertical: true,
  containInScrollParent: true,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'open': []
  'close': []
}>()

const containerRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLElement | null>(null)
const popoverRef = ref<HTMLElement | null>(null)
const isOpen = ref(props.modelValue ?? false)
/** Após flip automático: painel acima ou abaixo do trigger (só position top/bottom). */
const resolvedVerticalPlacement = ref<'top' | 'bottom'>('bottom')

const FALLBACK_POPOVER_HEIGHT_PX = 240
const BOUNDARY_PADDING_PX = 4

interface PopoverBoundary {
  top: number
  bottom: number
  left: number
  right: number
}

const popoverMaxHeightPx = ref<number | null>(null)

const popoverPosition = ref({
  top: 0,
  left: 0,
  triggerWidthPx: null as number | null,
})

function getPopoverBoundary(trigger: HTMLElement): PopoverBoundary {
  let element: HTMLElement | null = trigger.parentElement
  let firstScrollportBoundary: PopoverBoundary | null = null

  while (element && element !== document.documentElement) {
    if (element.hasAttribute('data-ecx-popover-boundary')) {
      const bounds = element.getBoundingClientRect()
      return {
        top: bounds.top,
        bottom: bounds.bottom,
        left: bounds.left,
        right: bounds.right,
      }
    }

    const style = window.getComputedStyle(element)
    const overflowY = style.overflowY
    const overflowX = style.overflowX
    const isVerticalScrollport =
      overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'
    const isHorizontalScrollport =
      overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay'

    if (
      firstScrollportBoundary === null &&
      (isVerticalScrollport || isHorizontalScrollport)
    ) {
      const bounds = element.getBoundingClientRect()
      firstScrollportBoundary = {
        top: bounds.top,
        bottom: bounds.bottom,
        left: bounds.left,
        right: bounds.right,
      }
    }

    element = element.parentElement
  }

  if (firstScrollportBoundary !== null) {
    return firstScrollportBoundary
  }

  return {
    top: 0,
    bottom: window.innerHeight,
    left: 0,
    right: window.innerWidth,
  }
}

function clampVerticalTopPx(
  placement: 'top' | 'bottom',
  rawTopPx: number,
  heightPx: number,
  boundary: PopoverBoundary,
): number {
  const innerTop = boundary.top + BOUNDARY_PADDING_PX
  const innerBottom = boundary.bottom - BOUNDARY_PADDING_PX
  const innerSpanPx = Math.max(0, innerBottom - innerTop)
  const safeHeightPx = Math.min(heightPx, innerSpanPx)

  if (placement === 'bottom') {
    let topPx = rawTopPx
    topPx = Math.max(topPx, innerTop)
    topPx = Math.min(topPx, innerBottom - safeHeightPx)
    return Math.max(topPx, innerTop)
  }

  let anchorBottomPx = rawTopPx
  anchorBottomPx = Math.min(anchorBottomPx, innerBottom)
  anchorBottomPx = Math.max(anchorBottomPx, innerTop + safeHeightPx)
  return anchorBottomPx
}

function clampHorizontalLeftPx(
  align: PopoverAlign,
  rawLeftPx: number,
  widthPx: number,
  boundary: PopoverBoundary,
): number {
  const innerLeft = boundary.left + BOUNDARY_PADDING_PX
  const innerRight = boundary.right - BOUNDARY_PADDING_PX
  if (align === 'start') {
    return Math.min(Math.max(rawLeftPx, innerLeft), innerRight - widthPx)
  }
  if (align === 'center') {
    const halfWidth = widthPx / 2
    return Math.min(
      Math.max(rawLeftPx, innerLeft + halfWidth),
      innerRight - halfWidth,
    )
  }
  return Math.min(Math.max(rawLeftPx, innerLeft + widthPx), innerRight)
}

/** Espaço livre entre o trigger e o limite, no lado em que o painel vai abrir. */
function availableHeightPx(
  placement: 'top' | 'bottom',
  triggerRect: DOMRect,
  boundary: PopoverBoundary,
  offsetPx: number,
): number {
  if (placement === 'bottom') {
    return boundary.bottom - BOUNDARY_PADDING_PX - (triggerRect.bottom + offsetPx)
  }
  return triggerRect.top - offsetPx - (boundary.top + BOUNDARY_PADDING_PX)
}

function pickVerticalPlacement(
  preferred: PopoverPosition,
  triggerRect: DOMRect,
  boundary: PopoverBoundary,
  popoverHeightPx: number,
  offsetPx: number,
): 'top' | 'bottom' {
  const spaceBelow = boundary.bottom - triggerRect.bottom - offsetPx
  const spaceAbove = triggerRect.top - boundary.top - offsetPx

  if (preferred === 'bottom') {
    if (spaceBelow >= popoverHeightPx) return 'bottom'
    if (spaceAbove >= popoverHeightPx) return 'top'
    return spaceAbove >= spaceBelow ? 'top' : 'bottom'
  }

  if (preferred === 'top') {
    if (spaceAbove >= popoverHeightPx) return 'top'
    if (spaceBelow >= popoverHeightPx) return 'bottom'
    return spaceAbove >= spaceBelow ? 'top' : 'bottom'
  }

  return 'bottom'
}

// Watch for external control
watch(() => props.modelValue, (newValue) => {
  if (newValue !== undefined) {
    isOpen.value = newValue
  }
})

let contentResizeObserver: ResizeObserver | null = null

/** Conteúdo carregado depois da abertura (lista async) muda a altura do painel. */
const observeContentResize = () => {
  contentResizeObserver?.disconnect()
  if (!popoverRef.value || typeof ResizeObserver === 'undefined') return
  contentResizeObserver = new ResizeObserver(() => updatePosition())
  contentResizeObserver.observe(popoverRef.value)
}

// Update position when opened
watch(isOpen, async (newValue) => {
  if (newValue) {
    await nextTick()
    updatePosition()
    observeContentResize()
    if (props.autoFlipVertical) {
      requestAnimationFrame(() => {
        if (isOpen.value) {
          updatePosition()
        }
      })
    }
    emit('open')
  } else {
    contentResizeObserver?.disconnect()
    contentResizeObserver = null
    popoverMaxHeightPx.value = null
    emit('close')
  }
  emit('update:modelValue', newValue)
})

const toggle = () => {
  isOpen.value = !isOpen.value
}

const open = () => {
  isOpen.value = true
}

const close = () => {
  isOpen.value = false
}

const updatePosition = () => {
  const trigger = triggerRef.value?.firstElementChild as HTMLElement || triggerRef.value
  if (!trigger) return
  
  const rect = trigger.getBoundingClientRect()
  const offset = props.offset

  popoverMaxHeightPx.value = null

  const needsVerticalBoundary =
    props.position === 'bottom' || props.position === 'top'

  const boundary =
    needsVerticalBoundary &&
    (props.containInScrollParent || props.autoFlipVertical)
      ? getPopoverBoundary(trigger)
      : null

  // Com `max-height` aplicado o painel para de crescer: `scrollHeight` mantém a altura
  // natural do conteúdo e evita alternar entre clampado e solto a cada recálculo.
  const naturalHeightPx = popoverRef.value
    ? Math.max(popoverRef.value.offsetHeight, popoverRef.value.scrollHeight)
    : 0

  let measuredHeight =
    naturalHeightPx > 0 ? naturalHeightPx : FALLBACK_POPOVER_HEIGHT_PX

  let placement: 'top' | 'bottom' =
    props.position === 'top' ? 'top' : 'bottom'

  if (
    props.autoFlipVertical &&
    (props.position === 'bottom' || props.position === 'top') &&
    boundary
  ) {
    placement = pickVerticalPlacement(
      props.position,
      rect,
      boundary,
      measuredHeight,
      offset,
    )
  }

  if (boundary && props.containInScrollParent && naturalHeightPx > 0) {
    const availableHeight = availableHeightPx(placement, rect, boundary, offset)
    if (availableHeight > 0 && measuredHeight > availableHeight) {
      popoverMaxHeightPx.value = availableHeight
      measuredHeight = availableHeight
    }
  }

  resolvedVerticalPlacement.value = placement
  
  let top = 0
  let left = 0
  
  switch (props.position) {
    case 'bottom':
    case 'top':
      top =
        placement === 'bottom' ? rect.bottom + offset : rect.top - offset
      break
    case 'left':
      left = rect.left - offset
      break
    case 'right':
      left = rect.right + offset
      break
  }
  
  // Calculate alignment
  if (props.position === 'bottom' || props.position === 'top') {
    switch (props.align) {
      case 'start':
        left = rect.left
        break
      case 'center':
        left = rect.left + rect.width / 2
        break
      case 'end':
        left = rect.right
        break
    }
  } else {
    switch (props.align) {
      case 'start':
        top = rect.top
        break
      case 'center':
        top = rect.top + rect.height / 2
        break
      case 'end':
        top = rect.bottom
        break
    }
  }

  if (boundary && props.containInScrollParent) {
    top = clampVerticalTopPx(placement, top, measuredHeight, boundary)
    const widthPx =
      popoverRef.value && popoverRef.value.offsetWidth > 0
        ? popoverRef.value.offsetWidth
        : null
    if (widthPx != null && widthPx > 0) {
      left = clampHorizontalLeftPx(props.align, left, widthPx, boundary)
    }
  }
  
  popoverPosition.value = {
    top,
    left,
    triggerWidthPx: props.matchTriggerWidth ? rect.width : null,
  }
}

const popoverStyle = computed((): CSSProperties => {
  const style: CSSProperties = {
    top: `${popoverPosition.value.top}px`,
  }

  if (popoverMaxHeightPx.value != null) {
    style.maxHeight = `${popoverMaxHeightPx.value}px`
  }

  const triggerWidthPx = popoverPosition.value.triggerWidthPx
  if (
    props.matchTriggerWidth &&
    triggerWidthPx != null &&
    triggerWidthPx > 0
  ) {
    style.width = `${triggerWidthPx}px`
  }

  // Adjust transform based on alignment
  if (props.position === 'bottom' || props.position === 'top') {
    switch (props.align) {
      case 'start':
        style.left = `${popoverPosition.value.left}px`
        break
      case 'center':
        style.left = `${popoverPosition.value.left}px`
        style.transform = 'translateX(-50%)'
        break
      case 'end':
        style.left = `${popoverPosition.value.left}px`
        style.transform = 'translateX(-100%)'
        break
    }
  }
  
  if (
    (props.position === 'bottom' || props.position === 'top') &&
    resolvedVerticalPlacement.value === 'top'
  ) {
    style.transform = (style.transform || '') + ' translateY(-100%)'
  }
  
  return style
})

const widthClass = computed(() => {
  if (props.matchTriggerWidth) return ''
  if (props.width.startsWith('w-')) return props.width
  return ''
})

// Handle click outside
const handleClickOutside = (event: MouseEvent) => {
  if (!props.closeOnClickOutside || !isOpen.value) return
  
  const target = event.target as Node
  const container = containerRef.value
  const popover = popoverRef.value
  
  if (container?.contains(target) || popover?.contains(target)) return
  
  close()
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('resize', updatePosition)
  window.addEventListener('scroll', updatePosition, true)
})

onUnmounted(() => {
  contentResizeObserver?.disconnect()
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', updatePosition)
  window.removeEventListener('scroll', updatePosition, true)
})

// Expose for external use
defineExpose({
  isOpen,
  toggle,
  open,
  close,
  updatePosition,
})
</script>

